import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'

import {
  getApiKeyByContactGroup,
  createApiKey,
  updateApiKey,
  getContactGroupUsers,
  removeApiKey
} from '../../../features/apiKeys/apiKeys'
import Dropdown from '../../../components/Dropdown/Dropdown'
import Input from '../../../components/Input/Input'
import { useMyContextLoading } from '../../../components/LoadingContextProvider/LoadingContextProvider'
import { showSnackBar } from '../../../components/Snackbar/Snackbar'
import ContainerPage from '../../../components/ContainerPage/ContainerPage'
import Button from '../../../components/Button/Button'
import { DoneIcon } from '../../../components/Icons/Icons'
import { LuCopy } from "react-icons/lu"
import OutboundWebhooks from './OutboundWebhooks'
import { selectFeatureFlag } from '../../../features/featureFlags/selectors'
import { LEAD_API_CREATOR_GUARD } from '../../../config/featureFlagKeys'


const WEBHOOKS_FLAG = 'WEBHOOK_SUBSCRIPTIONS'

const ManageApiKey = () => {
  const { groupId } = useParams()
  const dispatch = useDispatch()
  const webhooksEnabled = useSelector(selectFeatureFlag(WEBHOOKS_FLAG))
  const creatorGuard = useSelector(selectFeatureFlag(LEAD_API_CREATOR_GUARD))
  const { triggerLoading } = useMyContextLoading()

  const [copied, setCopied] = useState(false)

  const [defaultCreator, setDefaultCreator] = useState('')
  const [apiKey, setApiKey] = useState(null)
  const [users, setUsers] = useState([])
  const [usersLoaded, setUsersLoaded] = useState(false)
  const [loading, setLoading] = useState(false)

  const creatorMissing = creatorGuard && usersLoaded && Boolean(defaultCreator)
    && !users.some(u => u.value === defaultCreator)

  useEffect(() => {
    fetchApiKey()
    fetchGroupUsers()
  }, [])

  const fetchApiKey = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(getApiKeyByContactGroup({ groupId }))
      const data = response?.payload?.data
      if (data?.key) {
        setApiKey(data)
        setDefaultCreator(data?.defaultCreator)
      }
    } catch (error) {
      showSnackBar("error", error.message || 'Failed to load API key')
    }
    triggerLoading(false)
  }

  const fetchGroupUsers = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(getContactGroupUsers({ groupId }))
      if (Array.isArray(response?.payload?.data)) {
        const mapped = response?.payload?.data?.map((u) => ({
          label: `${u.firstName} ${u.lastName}`,
          value: u._id,
        }))
        setUsers(mapped)
        setUsersLoaded(true)

        if (defaultCreator && !mapped.some(u => u.value === defaultCreator)) {
          setDefaultCreator('')
        }
      }
    } catch (error) {
      showSnackBar("error", error.message || 'Failed to load group users')
    }
    triggerLoading(false)
  }

  const saveApiKey = async () => {
    if (loading) return
    setLoading(true)
    triggerLoading(true)
    try {
      const isExistingKey = !!apiKey?.key
      const action = isExistingKey ? updateApiKey : createApiKey
      const response = await dispatch(action({ groupId, defaultCreator }))
      const data = response?.payload?.data

      if (data?.key) {
        setApiKey(data)
        showSnackBar('success', isExistingKey ? 'API key updated successfully' : 'API key created successfully')
      } else {
        showSnackBar('error', 'Something went wrong, please try again later')
      }
    } catch (error) {
      showSnackBar('error', error.message || 'Something went wrong')
    }
    triggerLoading(false)
    setLoading(false)
  }

  const handleCopy = () => {
    if (apiKey?.key) {
      navigator.clipboard.writeText(apiKey.key)
      setCopied(true)
      showSnackBar('success', 'API Key copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleRevokeKey = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(removeApiKey({ groupId }))
      if (response?.payload?.data?.message) {
        setApiKey(null)
        setDefaultCreator('')
        showSnackBar("success", "API key revoked successfully")
      } else {
        showSnackBar("error", "Something went wrong while revoking the key")
      }
    } catch (error) {
      showSnackBar("error", error.message || 'Something went wrong')
    }
    triggerLoading(false)
  }

  return (
    <ContainerPage>
      <div className="p-6 w-full">
        <h2 className="text-xl font-semibold mb-4">
          {webhooksEnabled ? 'Integrations' : 'Manage API Key'}
        </h2>

        {webhooksEnabled && (
          <h3 className="text-lg font-semibold mb-1 text-center">
            Inbound — the key a partner uses to create leads here
          </h3>
        )}
        <div className="w-full flex justify-center">
          <div className="border border-divider bg-surface shadow-lg rounded-2xl w-[775px] px-8 py-6">
            <div className="mb-1">
              {apiKey?.createdAt && (
                <p className="text-sm text-text-secondary mb-2">
                  Created: {dayjs(apiKey.createdAt).format('YYYY-MM-DD HH:mm')}
                </p>
              )}
              {apiKey?.updatedAt && (
                <p className="text-sm text-text-secondary mb-2">
                  Updated: {dayjs(apiKey.updatedAt).format('YYYY-MM-DD HH:mm')}
                </p>
              )}
            </div>

            <div className="relative">
              <Input
                label="API Key"
                value={apiKey?.key || ''}
                readOnly
                disabled
                placeholder="No API key yet"
                classes="w-full"
                rightSideIcon={
                  apiKey?.key && (
                    <span
                      className="cursor-pointer flex items-center justify-center h-full px-3 mt-3.5"
                      onClick={handleCopy}
                    >
                      {copied ? <DoneIcon /> : <LuCopy size={18} className="text-text-secondary" />}
                    </span>
                  )
                }
              />
            </div>

            <div className="mt-4">
              {creatorMissing && (
                <div className="p-3 mb-3 bg-error-light border border-error-main text-error-main rounded text-sm">
                  The default creator of this key is no longer an active member of this office. Leads sent
                  without creatorEmail are refused. Pick a new default creator and press Update.
                </div>
              )}
              <Dropdown
                label="Default Creator"
                value={creatorMissing ? '' : defaultCreator}
                setValue={setDefaultCreator}
                menuItems={users}
                placeholder="Select default creator"
                className="w-full"
              />
            </div>

            <div className="mt-6 flex flex-row justify-end gap-4">
              {apiKey?.key && (
                <Button onClick={handleRevokeKey}
                  classes="!bg-error-main enabled:hover:!bg-error-main/90 flex flex-row">
                  Revoke API Key
                </Button>
              )}
              <Button onClick={saveApiKey} disabled={loading || !defaultCreator || creatorMissing}>
                {apiKey?.key ? 'Update' : 'Generate'} API Key
              </Button>
            </div>
          </div>
        </div>

        {webhooksEnabled && <OutboundWebhooks groupId={groupId} />}
      </div>
    </ContainerPage>
  )
}


export default ManageApiKey